import { useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SlideInMenu from '../partials/SlideInMenu';
import { AuthenticatedTemplate } from '@azure/msal-react';
import useBoolean from '../../hooks/use-boolean';
import Burger from '../shared/Burger';
import Logo from '../shared/Logo';
import { appConfig } from '../../config/appConfig';
import { useLocalAuth, isLocallyAuthenticated, localLogout, getLocalUser } from '../../auth/LocalAuthService';
import { resetToken } from '../../auth/UserService';
import PermissionService from '../../services/Permissions';

const HALF_URLS = [
    'bid',
    'template',
    'list-opportunity',
    'edit',
    'bidding-structure',
    'user-wizard',
    'schedule',
    'deal-line',
];

const MainMenu = ({ show }: { show: boolean }) => {
    const { t } = useTranslation(['f', 'b']),
        history = useHistory();

    if (!show) {
        return null;
    }

    return (
        <nav className="d-flex">
            <div className="navigation__lg-menu">
                <button
                    onClick={() => {
                        history.push('/data-link-or-transaction');
                    }}
                    className="navigation__top-links"
                >
                    {t('f:menu.overview')}
                </button>
                <span className="navigation__line ml-30"></span>
                <button
                    onClick={() => {
                        history.push('/overview');
                    }}
                    className="navigation__top-links"
                >
                    {t('f:menu.transaction')}
                </button>
                <span className="navigation__line ml-30"></span>
                <button
                    onClick={() => {
                        history.push('/data-analytics');
                    }}
                    className="navigation__top-links"
                >
                    {t('f:menu.dataAnalytics')}
                </button>
            </div>
        </nav>
    );
};

const Header = () => {
    // Add pages for light header
    const [isShown, setIsShownActions] = useBoolean(),
        [isHeaderRevealed, setIsHeaderRevealed] = useState(false),
        [hasShadow, setHasShadowActions] = useBoolean(true),
        [firstHalf, setFistHalf] = useState<Array<string>>([
            '/user-wizard/userDetails',
            '/user-wizard/companyDetails',
            '/user-wizard/preferences',
            '/property/add',
            '/property/add-single',
            '/property/add-portfolio',
            '/account-settings/company-details',
            '/account-settings/personal-details',
            '/account-settings/privacy&concent',
            '/account-settings/security',
        ]),
        location = useLocation(),
        history = useHistory(),
        pages = ['/data-link-or-transaction'];

    function isLightHeader(): Boolean {
        return pages.some((page) => location.pathname === page);
    }

    function isFirstHakfLightHeader(): Boolean {
        return firstHalf.some((page) => location.pathname === page);
    }

    useEffect(() => {
        const params = location.pathname.split('/');
        const id = params[2];
        const half = [...firstHalf];

        if (Number(id)) {
            if (HALF_URLS.includes(params[3])) {
                half.push(`${params.map((el) => el).join('/')}`);
            }
        }

        setFistHalf(half);
        if (Number(id)) {
            location.pathname === `/property/${id}` ? setHasShadowActions.setFalse() : setHasShadowActions.setTrue();
        }
    }, [location.pathname]);

    useEffect(() => {
        if (isShown) {
            setIsHeaderRevealed(true);
        }
    }, [isShown]);

    const showMenuIcon = () => {
        return (
            <Burger
                handleOnClick={() => {
                    setIsShownActions.toggle();
                }}
                classNameMain={isLightHeader() || isFirstHakfLightHeader() ? 'burger--light' : ''}
            />
        );
    }

    const localAuth = useLocalAuth();
    const localUser = getLocalUser();
    const hasLocalSession = isLocallyAuthenticated();
    const canLocalUserOpenMenu = hasLocalSession;
    const isLoginPage = location.pathname === '/login';
    const canUserOpenMenu = hasLocalSession
        ? canLocalUserOpenMenu
        : PermissionService.canViewAdminPanelMenu() || PermissionService.isDealRoomOnlyMsalUser();
    const hideMenuForUser = isLoginPage || (appConfig.hideMenu && !canUserOpenMenu);

    const handleSignOut = () => {
        localLogout();
        resetToken();
        history.push('/login');
    };

    return (
        <>
            <div
                className="header-reveal-zone"
                aria-hidden
                onMouseEnter={() => setIsHeaderRevealed(true)}
            />
            <header
                id="header"
                className={isHeaderRevealed || isShown ? 'header--revealed' : 'header--hidden'}
                onMouseEnter={() => setIsHeaderRevealed(true)}
                onMouseLeave={() => {
                    if (!isShown) setIsHeaderRevealed(false);
                }}
            >
                <div
                    className={`row-flex-clear navigation__container ${
                        isLightHeader() ? 'light' : hasShadow ? 'shadow' : ''
                    }`}
                >
                    {!hideMenuForUser && showMenuIcon()}
                    <Logo light={isLightHeader() || isFirstHakfLightHeader()} noMenu={hideMenuForUser} maxWidth={65} />

                    {localAuth && isLocallyAuthenticated() && (
                        <div className="header-user-meta">
                            <span
                                className="header-username"
                                style={{ cursor: 'pointer' }}
                                onClick={() => history.push('/profile')}
                            >
                                {localUser?.fullName || localUser?.username || localUser?.email || 'User'}
                            </span>
                            <button
                                className="header-signout-btn"
                                onClick={handleSignOut}
                            >
                                Sign Out
                            </button>
                        </div>
                    )}
                </div>
            </header>
            {!hideMenuForUser && (
                <SlideInMenu
                    setIsShown={setIsShownActions}
                    isShown={isShown}
                />
            )}
        </>
    );
};

export default Header;
